import { c as _c } from "react/compiler-runtime";
import { workflowsKeys } from "@/app/(v2)/workflows/_hooks/workflowsQueryKeys";
import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetchJson, getSelectedOrgId, setLastOrgId, setSelectedOrgId } from "@/lib/authenticatedFetch";
import { fetchInvites, fetchOrganizations } from "@/lib/orgs";
import { fetchSubscription, subscriptionKeys } from "@/hooks/useSubscription";
import { useConnectorsStore } from "@/stores/connectorsStore";
import { useWorkflowsStore } from "@/stores/workflowsStore";
import type { ClaimEmailInviteRequest, ClaimEmailInviteResponse, CreateOrganizationRequest, InviteByEmailRequest, InviteUserRequest, Organization, OrganizationInvite, OrganizationMember, PreviewEmailInviteResponse, UpdateInviteRoleRequest, UpdateMemberRoleRequest, UpdateOrganizationRequest } from "@/types/organization";
import { useAppAuth } from "@/hooks/useAppAuth";
import { getMasqueradeToken } from "@/lib/masquerade";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";
function subscribeMasqueradeToken(cb: () => void) {
  window.addEventListener("b3os-masquerade-changed", cb);
  return () => window.removeEventListener("b3os-masquerade-changed", cb);
}
function masqueradeTokenSnapshot() {
  return getMasqueradeToken();
}
function masqueradeTokenServerSnapshot() {
  return null;
}

/**
 * Query keys for organizations
 */
export const organizationKeys = {
  all: ["organizations"] as const,
  lists: () => [...organizationKeys.all, "list"] as const,
  list: (isMasquerading?: boolean) => [...organizationKeys.lists(), isMasquerading ? "masquerade" : "normal"] as const,
  invites: () => [...organizationKeys.all, "invites"] as const,
  selected: () => [...organizationKeys.all, "selected"] as const
};

/**
 * Hook for managing organizations with React Query
 */
export function useOrganizations() {
  const $ = _c(91);
  const queryClient = useQueryClient();
  const {
    isAuthenticated,
    isAuthenticating
  } = useAppAuth();
  const masqueradeToken = useSyncExternalStore(subscribeMasqueradeToken, masqueradeTokenSnapshot, masqueradeTokenServerSnapshot);
  let t0;
  if ($[0] !== masqueradeToken) {
    t0 = organizationKeys.list(!!masqueradeToken);
    $[0] = masqueradeToken;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== isAuthenticated || $[3] !== t0) {
    t1 = {
      queryKey: t0,
      queryFn: fetchOrganizations,
      enabled: isAuthenticated,
      staleTime: 300000
    };
    $[2] = isAuthenticated;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const {
    data: t2,
    isPending,
    isFetched,
    error
  } = useQuery(t1);
  let t3;
  if ($[5] !== t2) {
    t3 = t2 === undefined ? [] : t2;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const organizations = t3;
  const loading = isAuthenticating || isAuthenticated && isPending;
  const orgsLoaded = isAuthenticated ? isFetched : false;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      queryKey: organizationKeys.selected(),
      queryFn: _temp,
      initialData: _temp2,
      staleTime: 5000,
      gcTime: 10000
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    data: selectedOrgId
  } = useQuery(t4);
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = organizationKeys.invites();
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== isAuthenticated) {
    t6 = {
      queryKey: t5,
      queryFn: fetchInvites,
      enabled: isAuthenticated,
      refetchInterval: 30000,
      staleTime: 25000
    };
    $[9] = isAuthenticated;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const {
    data: t7,
    isLoading: invitesLoading
  } = useQuery(t6);
  let t8;
  if ($[11] !== t7) {
    t8 = t7 === undefined ? [] : t7;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const pendingInvites = t8;
  let t9;
  if ($[13] !== queryClient) {
    t9 = {
      mutationFn: _temp3,
      onSuccess: async (newOrg, variables, context) => {
        const currentOrgId = getSelectedOrgId();
        if (!currentOrgId && newOrg?.id) {
          setSelectedOrgId(newOrg.id);
          setLastOrgId(newOrg.id);
          console.log("@@B3OS: Auto-selected newly created organization:", newOrg.id);
        }
        await Promise.all([queryClient.invalidateQueries({
          queryKey: organizationKeys.list()
        }), queryClient.invalidateQueries({
          queryKey: organizationKeys.selected()
        })]);
      }
    };
    $[13] = queryClient;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const createOrgMutation = useMutation(t9);
  let t10;
  if ($[15] !== queryClient) {
    t10 = {
      mutationFn: _temp4,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: organizationKeys.list()
        });
      }
    };
    $[15] = queryClient;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const updateOrgMutation = useMutation(t10);
  let t11;
  if ($[17] !== queryClient) {
    t11 = {
      mutationFn: _temp5,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: organizationKeys.list()
        });
        queryClient.invalidateQueries({
          queryKey: organizationKeys.selected()
        });
      }
    };
    $[17] = queryClient;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const deleteOrgMutation = useMutation(t11);
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      mutationFn: _temp6
    };
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const inviteUserMutation = useMutation(t12);
  let t13;
  if ($[20] !== queryClient) {
    t13 = {
      mutationFn: _temp7,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: organizationKeys.list()
        });
        queryClient.invalidateQueries({
          queryKey: organizationKeys.invites()
        });
      }
    };
    $[20] = queryClient;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const acceptInviteMutation = useMutation(t13);
  let t14;
  if ($[22] !== queryClient) {
    t14 = {
      mutationFn: _temp8,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: organizationKeys.invites()
        });
      }
    };
    $[22] = queryClient;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const declineInviteMutation = useMutation(t14);
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      mutationFn: _temp9
    };
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const inviteByEmailMutation = useMutation(t15);
  let t16;
  if ($[25] !== queryClient) {
    t16 = {
      mutationFn: _temp10,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: organizationKeys.invites()
        });
      }
    };
    $[25] = queryClient;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  const resendInviteMutation = useMutation(t16);
  let t17;
  if ($[27] !== queryClient) {
    t17 = {
      mutationFn: _temp11,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: organizationKeys.invites()
        });
      }
    };
    $[27] = queryClient;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  const updateInviteRoleMutation = useMutation(t17);
  let t18;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      mutationFn: _temp12
    };
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  const updateMemberRoleMutation = useMutation(t18);
  let t19;
  if ($[30] !== queryClient) {
    t19 = {
      mutationFn: _temp13,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: organizationKeys.list()
        });
        queryClient.invalidateQueries({
          queryKey: organizationKeys.invites()
        });
      }
    };
    $[30] = queryClient;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  const claimEmailInviteMutation = useMutation(t19);
  let t20;
  if ($[32] !== organizations || $[33] !== queryClient) {
    t20 = orgId_6 => {
      setSelectedOrgId(orgId_6);
      setLastOrgId(orgId_6);
      const org = organizations.find(o => o.id === orgId_6);
      if (org?.name) {
        localStorage.setItem(`b3os-org-name-${orgId_6}`, org.name);
      }
      queryClient.invalidateQueries({
        queryKey: organizationKeys.selected()
      });
      useWorkflowsStore.getState().reset();
      useConnectorsStore.getState().reset();
      queryClient.invalidateQueries({
        queryKey: workflowsKeys.all
      });
      queryClient.prefetchQuery({
        queryKey: subscriptionKeys.detail(orgId_6),
        queryFn: () => fetchSubscription(orgId_6),
        staleTime: 60000
      });
    };
    $[32] = organizations;
    $[33] = queryClient;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  const selectOrg = t20;
  const t21 = error as Error | null;
  let t22;
  if ($[35] !== organizations) {
    t22 = async () => organizations;
    $[35] = organizations;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] !== createOrgMutation) {
    t23 = async data_6 => await createOrgMutation.mutateAsync(data_6);
    $[37] = createOrgMutation;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  let t24;
  if ($[39] !== updateOrgMutation) {
    t24 = async (orgId_7, data_7) => await updateOrgMutation.mutateAsync({
      orgId: orgId_7,
      data: data_7
    });
    $[39] = updateOrgMutation;
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  let t25;
  if ($[41] !== deleteOrgMutation) {
    t25 = async orgId_8 => await deleteOrgMutation.mutateAsync(orgId_8);
    $[41] = deleteOrgMutation;
    $[42] = t25;
  } else {
    t25 = $[42];
  }
  let t26;
  if ($[43] !== inviteUserMutation) {
    t26 = async (orgId_9, data_8) => await inviteUserMutation.mutateAsync({
      orgId: orgId_9,
      data: data_8
    });
    $[43] = inviteUserMutation;
    $[44] = t26;
  } else {
    t26 = $[44];
  }
  let t27;
  if ($[45] !== acceptInviteMutation) {
    t27 = async inviteId_3 => await acceptInviteMutation.mutateAsync(inviteId_3);
    $[45] = acceptInviteMutation;
    $[46] = t27;
  } else {
    t27 = $[46];
  }
  let t28;
  if ($[47] !== declineInviteMutation) {
    t28 = async inviteId_4 => await declineInviteMutation.mutateAsync(inviteId_4);
    $[47] = declineInviteMutation;
    $[48] = t28;
  } else {
    t28 = $[48];
  }
  let t29;
  if ($[49] !== inviteByEmailMutation) {
    t29 = async (orgId_10, data_9) => await inviteByEmailMutation.mutateAsync({
      orgId: orgId_10,
      data: data_9
    });
    $[49] = inviteByEmailMutation;
    $[50] = t29;
  } else {
    t29 = $[50];
  }
  let t30;
  if ($[51] !== resendInviteMutation) {
    t30 = async (orgId_11, inviteId_5) => await resendInviteMutation.mutateAsync({
      orgId: orgId_11,
      inviteId: inviteId_5
    });
    $[51] = resendInviteMutation;
    $[52] = t30;
  } else {
    t30 = $[52];
  }
  let t31;
  if ($[53] !== updateInviteRoleMutation) {
    t31 = async (orgId_12, inviteId_6, data_10) => await updateInviteRoleMutation.mutateAsync({
      orgId: orgId_12,
      inviteId: inviteId_6,
      data: data_10
    });
    $[53] = updateInviteRoleMutation;
    $[54] = t31;
  } else {
    t31 = $[54];
  }
  let t32;
  if ($[55] !== updateMemberRoleMutation) {
    t32 = async (orgId_13, userId_0, data_11) => await updateMemberRoleMutation.mutateAsync({
      orgId: orgId_13,
      userId: userId_0,
      data: data_11
    });
    $[55] = updateMemberRoleMutation;
    $[56] = t32;
  } else {
    t32 = $[56];
  }
  let t33;
  if ($[57] !== claimEmailInviteMutation) {
    t33 = async data_12 => await claimEmailInviteMutation.mutateAsync(data_12);
    $[57] = claimEmailInviteMutation;
    $[58] = t33;
  } else {
    t33 = $[58];
  }
  let t34;
  if ($[59] !== queryClient) {
    t34 = async () => {
      const data_13 = await queryClient.fetchQuery({
        queryKey: organizationKeys.invites(),
        queryFn: fetchInvites
      });
      return data_13;
    };
    $[59] = queryClient;
    $[60] = t34;
  } else {
    t34 = $[60];
  }
  let t35;
  if ($[61] !== acceptInviteMutation.isPending || $[62] !== claimEmailInviteMutation.isPending || $[63] !== createOrgMutation.isPending || $[64] !== declineInviteMutation.isPending || $[65] !== deleteOrgMutation.isPending || $[66] !== inviteByEmailMutation.isPending || $[67] !== inviteUserMutation.isPending || $[68] !== invitesLoading || $[69] !== loading || $[70] !== organizations || $[71] !== orgsLoaded || $[72] !== pendingInvites || $[73] !== selectOrg || $[74] !== selectedOrgId || $[75] !== t21 || $[76] !== t22 || $[77] !== t23 || $[78] !== t24 || $[79] !== t25 || $[80] !== t26 || $[81] !== t27 || $[82] !== t28 || $[83] !== t29 || $[84] !== t30 || $[85] !== t31 || $[86] !== t32 || $[87] !== t33 || $[88] !== t34 || $[89] !== updateOrgMutation.isPending) {
    t35 = {
      organizations,
      selectedOrgId,
      loading,
      orgsLoaded,
      error: t21,
      pendingInvites,
      invitesLoading,
      selectOrg,
      fetchUserOrgs: t22,
      createOrg: t23,
      updateOrg: t24,
      deleteOrg: t25,
      inviteUserToOrg: t26,
      acceptInvite: t27,
      declineInvite: t28,
      inviteByEmail: t29,
      resendInvite: t30,
      updateInviteRole: t31,
      updateMemberRole: t32,
      claimEmailInvite: t33,
      previewEmailInvite: _temp14,
      getMyInvites: t34,
      isCreating: createOrgMutation.isPending,
      isUpdating: updateOrgMutation.isPending,
      isDeleting: deleteOrgMutation.isPending,
      isInviting: inviteUserMutation.isPending,
      isAccepting: acceptInviteMutation.isPending,
      isDeclining: declineInviteMutation.isPending,
      isInvitingByEmail: inviteByEmailMutation.isPending,
      isClaimingInvite: claimEmailInviteMutation.isPending
    };
    $[61] = acceptInviteMutation.isPending;
    $[62] = claimEmailInviteMutation.isPending;
    $[63] = createOrgMutation.isPending;
    $[64] = declineInviteMutation.isPending;
    $[65] = deleteOrgMutation.isPending;
    $[66] = inviteByEmailMutation.isPending;
    $[67] = inviteUserMutation.isPending;
    $[68] = invitesLoading;
    $[69] = loading;
    $[70] = organizations;
    $[71] = orgsLoaded;
    $[72] = pendingInvites;
    $[73] = selectOrg;
    $[74] = selectedOrgId;
    $[75] = t21;
    $[76] = t22;
    $[77] = t23;
    $[78] = t24;
    $[79] = t25;
    $[80] = t26;
    $[81] = t27;
    $[82] = t28;
    $[83] = t29;
    $[84] = t30;
    $[85] = t31;
    $[86] = t32;
    $[87] = t33;
    $[88] = t34;
    $[89] = updateOrgMutation.isPending;
    $[90] = t35;
  } else {
    t35 = $[90];
  }
  return t35;
}

/**
 * Hook to invalidate organizations query from anywhere
 */
async function _temp14(token) {
  const response_6 = await authenticatedFetchJson(`${V2_ENDPOINT}/v1/invites/preview?token=${encodeURIComponent(token)}`, {
    skipOrgId: true
  });
  return response_6.data;
}
async function _temp13(data_5) {
  const response_5 = await authenticatedFetchJson(`${V2_ENDPOINT}/v1/invites/claim`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(data_5),
    skipOrgId: true
  });
  return response_5.data;
}
async function _temp12(t0) {
  const {
    orgId: orgId_5,
    userId,
    data: data_4
  } = t0;
  const response_4 = await authenticatedFetchJson(`${V2_ENDPOINT}/v1/organizations/:orgId/members/${userId}/role`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(data_4),
    orgId: orgId_5
  });
  return response_4.data;
}
async function _temp11(t0) {
  const {
    orgId: orgId_4,
    inviteId: inviteId_2,
    data: data_3
  } = t0;
  const response_3 = await authenticatedFetchJson(`${V2_ENDPOINT}/v1/organizations/:orgId/invites/${inviteId_2}/role`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(data_3),
    orgId: orgId_4
  });
  return response_3.data;
}
async function _temp10(t0) {
  const {
    orgId: orgId_3,
    inviteId: inviteId_1
  } = t0;
  await authenticatedFetchJson(`${V2_ENDPOINT}/v1/organizations/:orgId/invites/${inviteId_1}/resend`, {
    method: "POST",
    orgId: orgId_3
  });
}
async function _temp9(t0) {
  const {
    orgId: orgId_2,
    data: data_2
  } = t0;
  const response_2 = await authenticatedFetchJson(`${V2_ENDPOINT}/v1/organizations/:orgId/invites/email`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(data_2),
    orgId: orgId_2
  });
  return response_2.data;
}
async function _temp8(inviteId_0) {
  await authenticatedFetchJson(`${V2_ENDPOINT}/v1/invites/${inviteId_0}/decline`, {
    method: "POST",
    skipOrgId: true
  });
}
async function _temp7(inviteId) {
  await authenticatedFetchJson(`${V2_ENDPOINT}/v1/invites/${inviteId}/accept`, {
    method: "POST",
    skipOrgId: true
  });
}
async function _temp6(t0) {
  const {
    orgId: orgId_1,
    data: data_1
  } = t0;
  const response_1 = await authenticatedFetchJson(`${V2_ENDPOINT}/v1/organizations/:orgId/invites`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(data_1),
    orgId: orgId_1
  });
  return response_1.data;
}
async function _temp5(orgId_0) {
  await authenticatedFetchJson(`${V2_ENDPOINT}/v1/organizations/${orgId_0}`, {
    method: "DELETE",
    skipOrgId: true
  });
}
async function _temp4(t0) {
  const {
    orgId,
    data: data_0
  } = t0;
  const response_0 = await authenticatedFetchJson(`${V2_ENDPOINT}/v1/organizations/${orgId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(data_0),
    skipOrgId: true
  });
  return response_0.data;
}
async function _temp3(data) {
  const response = await authenticatedFetchJson(`${V2_ENDPOINT}/v1/organizations`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(data),
    skipOrgId: true
  });
  return response.data;
}
function _temp2() {
  return getSelectedOrgId() ?? null;
}
function _temp() {
  return getSelectedOrgId() ?? null;
}
export function useInvalidateOrganizations() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = () => {
      queryClient.invalidateQueries({
        queryKey: organizationKeys.list()
      });
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}